import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import {
  connectNotificationSocket,
  disconnectNotificationSocket,
} from "../config/socket";
import { SOCKET_EVENTS } from "../config/socket/events";
import {
  receiveNotification,
  setNotificationReadState,
  markAllNotificationsReadInState,
  fetchNotifications,
} from "../redux/slice/notificationSlice";
import { EMPTY_PREVIEW } from "../config/api/emptyResponses";

const NOTIFICATION_QUERY_KEY = ["notifications"];
const UNREAD_QUERY_KEY = ["notifications-unread"];

export function useNotificationSocket(token) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!token) {
      disconnectNotificationSocket();
      return undefined;
    }

    if (EMPTY_PREVIEW) {
      dispatch(fetchNotifications(token));
      return undefined;
    }

    const socket = connectNotificationSocket(token);
    if (!socket) return undefined;

    dispatch(fetchNotifications(token));

    const invalidateNotificationQueries = () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_QUERY_KEY });
    };

    const onConnected = () => {
      dispatch(fetchNotifications(token));
    };

    const onNotificationNew = (payload) => {
      const notification = payload?.notification;
      if (!notification) return;
      dispatch(receiveNotification(notification));
      invalidateNotificationQueries();
    };

    const onNotificationRead = (payload) => {
      if (payload?.all) {
        dispatch(markAllNotificationsReadInState());
        invalidateNotificationQueries();
        return;
      }

      const id = payload?.id;
      if (!id) return;

      dispatch(
        setNotificationReadState({
          id,
          isRead: payload?.isRead !== undefined ? Boolean(payload.isRead) : true,
        })
      );
      invalidateNotificationQueries();
    };

    socket.on(SOCKET_EVENTS.CONNECTED, onConnected);
    socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, onNotificationNew);
    socket.on(SOCKET_EVENTS.NOTIFICATION_READ, onNotificationRead);

    return () => {
      socket.off(SOCKET_EVENTS.CONNECTED, onConnected);
      socket.off(SOCKET_EVENTS.NOTIFICATION_NEW, onNotificationNew);
      socket.off(SOCKET_EVENTS.NOTIFICATION_READ, onNotificationRead);
    };
  }, [token, dispatch, queryClient]);
}

export default useNotificationSocket;
